import { generateBoxKeyPair, wrapBoxSecretKey, unwrapBoxSecretKey, unwrapKeyChain, unwrapKeyFromOwner, unwrapSpaceKey, unwrapNotebookKeyFromSpace, wrapPreviousKey, buildShareLink, wrapNotebookKeyForSpace, wrapSpaceKey, wrapKeyForMember } from './chunk-3EMOFRLG.js';
import { getNoteMetas, patchNoteMeta, accountRoots, getSharedRefs, putSharedRef, deleteSharedRef, getSharedSpaceRefs, putSharedSpaceRef, deleteSharedSpaceRef, getNotebookRef, putSpaceNotebookRef, deleteNotebookRef, getSpaceNotebookRef, putNotebookRef, deleteSpaceNotebookRef, getNoteMeta } from './chunk-7U33VFN2.js';
import { API_PREFIX, API_ROUTES, classifyApiFailure, notebookKeyResponseSchema, spaceNotebookSchema, spaceKeyResponseSchema, sharedSpaceSchema, shareSchema, accountSharedByMeItemSchema, accountSharedItemSchema, sharedNoteKeyResponseSchema, memberSchema, accountIncomingInvitationSchema, outgoingInvitationSchema, publicKeyResponseSchema, boxKeySchema } from './chunk-PLMCPVKY.js';
import { unwrapKey, CryptoError, generateKey, wrapKey } from './chunk-PXX4LS7M.js';
import { AD } from './chunk-SA27ENP5.js';
import { uuidv7 } from './chunk-RPUENDSP.js';
import { toBase64Url, fromBase64Url } from './chunk-LAMR6P2E.js';
import { z } from 'zod';

// src/sharing/http.ts
var ApiRequestError = class extends Error {
  constructor(route, failure) {
    super(
      `${route}: ${failure.kind}${failure.kind === "error" ? ` ${failure.status} ${failure.message}` : ""}`
    );
    this.route = route;
    this.failure = failure;
    this.name = "ApiRequestError";
  }
  route;
  failure;
  /** HTTP status, or null for network failures. */
  get status() {
    if (this.failure.kind === "error") return this.failure.status;
    if (this.failure.kind === "network") return null;
    return 401;
  }
};
function createHttpClient(options) {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const base = options.baseUrl.replace(/\/+$/, "") + API_PREFIX;
  return async (route, { params = {}, query, body, schema, nullOn } = {}) => {
    const { method, path } = API_ROUTES[route];
    const filled = path.replace(/:(\w+)/g, (_, name) => {
      const value = params[name];
      if (value === void 0) throw new Error(`${route}: missing path parameter ${name}`);
      return encodeURIComponent(value);
    });
    const qs = query ? `?${new URLSearchParams(query).toString()}` : "";
    const headers = {
      authorization: `Bearer ${await options.accessToken()}`
    };
    if (body !== void 0) headers["content-type"] = "application/json";
    let res2;
    try {
      res2 = await doFetch(base + filled + qs, {
        method,
        headers,
        ...body !== void 0 ? { body: JSON.stringify(body) } : {}
      });
    } catch {
      throw new ApiRequestError(route, classifyApiFailure(null, null));
    }
    const text = await res2.text().catch(() => "");
    if (!res2.ok) {
      if (nullOn?.includes(res2.status)) return null;
      throw new ApiRequestError(route, classifyApiFailure(res2.status, text));
    }
    if (!schema) return void 0;
    let json;
    try {
      json = JSON.parse(text);
    } catch {
      json = void 0;
    }
    const parsed = schema.safeParse(json);
    if (!parsed.success) {
      throw new ApiRequestError(route, {
        kind: "error",
        status: res2.status,
        code: null,
        message: `invalid response: ${parsed.error.message}`
      });
    }
    return parsed.data;
  };
}
var target = (t) => ({ kind: t.kind, id: t.id });
var isD54 = (i) => i.target.kind !== "space";
function createSharingApi(http) {
  return {
    /** null when the account has no key pair yet (404). */
    getBoxKey: () => http("getBoxKey", { schema: boxKeySchema, nullOn: [404] }),
    putBoxKey: (body) => http("putBoxKey", { body }),
    /** null when the account is not related to the caller (404, D-54). */
    getPublicKey: (userId) => http("getPublicKey", {
      params: { userId },
      schema: publicKeyResponseSchema,
      nullOn: [404]
    }),
    /** Spaces too (D-62): `target.kind = "space"`. */
    createInvitation: (body) => http("createInvitation", { body }),
    listInvitations: (t) => http("listInvitations", {
      query: { target: `${t.kind}:${t.id}` },
      schema: z.array(outgoingInvitationSchema)
    }),
    deleteInvitation: (id) => http("deleteInvitation", { params: { id } }),
    /** Invitations to notebooks and notes (D-54); spaces: `incomingInvitationsAll`. */
    incomingInvitations: async () => (await http("incomingInvitations", { schema: z.array(accountIncomingInvitationSchema) })).filter(isD54),
    incomingInvitationsAll: () => http("incomingInvitations", { schema: z.array(accountIncomingInvitationSchema) }),
    /** The accepted resource — a space too (D-62). */
    acceptInvitation: (id) => http("acceptInvitation", { params: { id }, body: {}, schema: accountSharedItemSchema }),
    declineInvitation: (id) => http("declineInvitation", { params: { id }, body: {} }),
    listMembers: (t) => http("listMembers", { params: target(t), schema: z.array(memberSchema) }),
    setMemberRole: (t, userId, role) => http("patchMember", { params: { ...target(t), userId }, body: { role } }),
    /** The owner removes a member; with the caller's own userId — «Выйти». */
    removeMember: (t, userId) => http("deleteMember", { params: { ...target(t), userId } }),
    putMemberKey: (t, userId, body) => http("putMemberKey", { params: { ...target(t), userId }, body }),
    rotateKey: (t, body) => http("rotateKey", { params: target(t), body }),
    rotateKeyDone: (t) => http("rotateKeyDone", { params: target(t), body: {} }),
    getSharedNoteKey: (noteId) => http("getSharedNoteKey", { params: { id: noteId }, schema: sharedNoteKeyResponseSchema }),
    /** Notebooks and notes shared with me (D-54); shared spaces are left out. */
    sharedWithMe: async () => (await http("sharedWithMe", { schema: z.array(accountSharedItemSchema) })).filter(isD54),
    sharedByMe: async () => (await http("sharedByMe", { schema: z.array(accountSharedByMeItemSchema) })).filter(isD54),
    /** Everything, shared spaces included (D-62). */
    sharedWithMeAll: () => http("sharedWithMe", { schema: z.array(accountSharedItemSchema) }),
    sharedByMeAll: () => http("sharedByMe", { schema: z.array(accountSharedByMeItemSchema) }),
    listShares: () => http("listShares", { schema: z.array(shareSchema) }),
    createShare: (body) => http("createShare", { body, schema: shareSchema }),
    deleteShare: (id) => http("deleteShare", { params: { id } }),
    // Shared spaces (D-62).
    createSpace: (body) => http("createSpace", { body, schema: sharedSpaceSchema }),
    listSpaces: () => http("listSpaces", { schema: z.array(sharedSpaceSchema) }),
    getSpaceKey: (spaceId) => http("getSpaceKey", { params: { id: spaceId }, schema: spaceKeyResponseSchema }),
    listSpaceNotebooks: (spaceId) => http("listSpaceNotebooks", {
      params: { id: spaceId },
      schema: z.array(spaceNotebookSchema)
    }),
    putSpaceNotebook: (spaceId, notebookId, body) => http("putSpaceNotebook", { params: { id: spaceId, notebookId }, body }),
    moveOutSpaceNotebook: (spaceId, notebookId, body) => http("moveOutSpaceNotebook", { params: { id: spaceId, notebookId }, body }),
    rotateSpace: (spaceId, body) => http("rotateSpace", { params: { id: spaceId }, body }),
    rotateSpaceDone: (spaceId) => http("rotateSpaceDone", { params: { id: spaceId }, body: {} }),
    deleteSpace: (spaceId) => http("deleteSpace", { params: { id: spaceId } }),
    getNotebookKey: (notebookId) => http("getNotebookKey", { params: { id: notebookId }, schema: notebookKeyResponseSchema })
  };
}

// src/sharing/keys.ts
async function ensureAccountKeyPair(api, userId, masterKey) {
  const existing = await api.getBoxKey();
  if (existing) return openBoxKey(existing, userId, masterKey);
  const pair = generateBoxKeyPair();
  const body = {
    publicKey: toBase64Url(pair.publicKey),
    wrappedSecretKey: wrapBoxSecretKey(pair.secretKey, masterKey, userId)
  };
  try {
    await api.putBoxKey(body);
    return pair;
  } catch (e) {
    if (e instanceof ApiRequestError && e.status === 409) {
      const winner = await api.getBoxKey();
      if (winner) return openBoxKey(winner, userId, masterKey);
    }
    throw e;
  }
}
function openBoxKey(key, userId, masterKey) {
  const publicKey = fromBase64Url(key.publicKey);
  return {
    publicKey,
    secretKey: unwrapBoxSecretKey(key.wrappedSecretKey, masterKey, userId, publicKey)
  };
}
function ownerNotebookKeys(res2, masterKey, notebookId) {
  const version = res2.version ?? 1;
  const current = unwrapKey(masterKey, res2.wrappedKey, AD.notebookKeyVersion(notebookId, version));
  return {
    version,
    keys: unwrapKeyChain(current, version, res2.keyChain ?? [], "notebook", notebookId)
  };
}
function sharedItemKeys(item, me) {
  if (!item.wrappedKey) return null;
  const { kind, id } = item.target;
  const current = unwrapKeyFromOwner(
    item.wrappedKey,
    me.secretKey,
    fromBase64Url(item.owner.publicKey),
    {
      kind,
      targetId: id,
      version: item.keyVersion,
      fromUserId: item.owner.userId,
      toUserId: me.userId
    }
  );
  return {
    version: item.keyVersion,
    keys: unwrapKeyChain(current, item.keyVersion, item.keyChain, kind, id)
  };
}
function noteKeysFromMeta(meta, notebookKeys) {
  const version = meta.keyVersion ?? 1;
  const ad = AD.noteKeyVersion(meta.id, version);
  for (let i = 0; i < notebookKeys.length; i++) {
    let current;
    try {
      current = unwrapKey(notebookKeys[i], meta.wrappedKey, ad);
    } catch {
      continue;
    }
    return {
      version,
      keys: unwrapKeyChain(current, version, meta.keyChain ?? [], "note", meta.id),
      notebookIndex: i
    };
  }
  throw new CryptoError("decrypt_failed", `no notebook key opens the key of note ${meta.id}`);
}
function ownerSpaceKeys(res2, masterKey, spaceId) {
  const current = unwrapSpaceKey(masterKey, res2.wrappedKey, spaceId, res2.version);
  return {
    version: res2.version,
    keys: unwrapKeyChain(current, res2.version, res2.keyChain, "space", spaceId)
  };
}
function spaceNotebookKeys(entry, spaceKeys, spaceId) {
  for (const spaceKey of spaceKeys) {
    let current;
    try {
      current = unwrapNotebookKeyFromSpace(
        spaceKey,
        entry.spaceWrappedKey,
        spaceId,
        entry.notebookId,
        entry.version
      );
    } catch {
      continue;
    }
    return {
      version: entry.version,
      keys: unwrapKeyChain(current, entry.version, entry.keyChain, "notebook", entry.notebookId)
    };
  }
  throw new CryptoError(
    "decrypt_failed",
    `no key of space ${spaceId} opens the key of notebook ${entry.notebookId}`
  );
}

// src/sharing/members.ts
function wrapFor(target2, key, version, owner, userId, publicKey) {
  return wrapKeyForMember(key, owner.secretKey, fromBase64Url(publicKey), {
    kind: target2.kind,
    targetId: target2.id,
    version,
    fromUserId: owner.userId,
    toUserId: userId
  });
}
var MemberNotConfirmedError = class extends Error {
  constructor(member, reason) {
    super(`key not given to ${member.userId}: ${reason}`);
    this.member = member;
    this.reason = reason;
    this.name = "MemberNotConfirmedError";
  }
  member;
  reason;
};
async function wrapForMembers(api, target2, key, version, owner, members, vet) {
  const out = [];
  for (const m of members) {
    const userId = typeof m === "string" ? m : m.userId;
    const pk = await api.getPublicKey(userId);
    if (!pk) throw new Error(`no public key for member ${userId}`);
    if (vet && typeof m !== "string") {
      const reason = await vet.vetting.check(target2, m, pk.publicKey);
      if (reason && !await vet.confirm?.(m, reason)) throw new MemberNotConfirmedError(m, reason);
    }
    out.push({ userId, wrappedKey: wrapFor(target2, key, version, owner, userId, pk.publicKey) });
    if (vet) await vet.vetting.given(target2, userId, pk.publicKey);
  }
  return out;
}
async function deliverAwaitingKeys(options) {
  const { api, target: target2, keys, owner } = options;
  const waiting = (await api.listMembers(target2)).filter(
    (m) => m.status === "awaiting_key" && m.userId !== owner.userId
  );
  const delivered = [];
  for (const member of waiting) {
    const pk = await api.getPublicKey(member.userId);
    if (!pk) throw new Error(`no public key for member ${member.userId}`);
    const reason = await options.vetting?.check(target2, member, pk.publicKey) ?? (member.accountCreatedAfterInvite ? "late_account" : null);
    if (reason && !await options.confirm?.(member, reason)) continue;
    await api.putMemberKey(target2, member.userId, {
      version: keys.version,
      wrappedKey: wrapFor(target2, keys.keys[0], keys.version, owner, member.userId, pk.publicKey)
    });
    await options.vetting?.given(target2, member.userId, pk.publicKey);
    delivered.push(member);
  }
  return delivered;
}

// src/sharing/rotation.ts
async function activeMembers(api, target2, ownerId) {
  return (await api.listMembers(target2)).filter(
    (m) => m.status === "active" && m.userId !== ownerId
  );
}
async function rotateNotebookKey(options) {
  const { api, notebookId, owner, masterKey, index } = options;
  const target2 = { kind: "notebook", id: notebookId };
  const res2 = await api.getNotebookKey(notebookId);
  let notebook = ownerNotebookKeys(res2, masterKey, notebookId);
  if (!res2.keyRotationPending && !options.force) {
    return { rotated: false, newVersion: false, notebook, rekeyedNotes: 0 };
  }
  const metas = getNoteMetas(index, { includeTrashed: true });
  const behind = (m) => noteKeysFromMeta(m, notebook.keys).notebookIndex > 0;
  const since = res2.pendingSinceVersion;
  const resuming = !res2.keyRotationPending ? false : since != null ? notebook.version > since : metas.some(behind);
  if (!resuming) {
    const version = notebook.version + 1;
    const key = generateKey();
    const members = await activeMembers(api, target2, owner.userId);
    await api.rotateKey(target2, {
      version,
      ownerWrappedKey: wrapKey(masterKey, key, AD.notebookKeyVersion(notebookId, version)),
      previousKeyWrap: wrapPreviousKey(key, notebook.keys[0], "notebook", notebookId, version),
      memberKeys: await wrapForMembers(
        api,
        target2,
        key,
        version,
        owner,
        members,
        options.vetting ? { vetting: options.vetting, ...options.confirm ? { confirm: options.confirm } : {} } : void 0
      ),
      ...options.agentMode ? { notebookKey: toBase64Url(key) } : {}
    });
    notebook = { version, keys: [key, ...notebook.keys] };
    await options.onKeys({ notebook });
  }
  const notes = rekeyNotes(index, notebook);
  await options.whenIndexSent();
  if (notes.size) await options.onKeys({ notes });
  await api.rotateKeyDone(target2);
  return { rotated: true, newVersion: !resuming, notebook, rekeyedNotes: notes.size };
}
function rekeyNotes(index, notebook) {
  const notes = /* @__PURE__ */ new Map();
  index.transact(() => {
    for (const meta of getNoteMetas(index, { includeTrashed: true })) {
      const old = noteKeysFromMeta(meta, notebook.keys);
      if (old.notebookIndex === 0) continue;
      const version = old.version + 1;
      const key = generateKey();
      patchNoteMeta(index, meta.id, {
        wrappedKey: wrapKey(notebook.keys[0], key, AD.noteKeyVersion(meta.id, version)),
        keyVersion: version,
        keyChain: [
          wrapPreviousKey(key, old.keys[0], "note", meta.id, version),
          ...meta.keyChain ?? []
        ]
      });
      notes.set(meta.id, { version, keys: [key, ...old.keys] });
    }
  });
  return notes;
}
async function recreateShareLinks(options) {
  const { api, target: target2 } = options;
  const shares = (await api.listShares()).filter(
    (s) => s.target.kind === target2.kind && s.target.id === target2.id
  );
  const out = [];
  for (const old of shares) {
    const share = await api.createShare({
      shareId: (options.newId ?? uuidv7)(),
      target: old.target,
      permission: old.permission,
      expiresAt: old.expiresAt
    });
    await api.deleteShare(old.id);
    out.push({
      oldId: old.id,
      share,
      url: buildShareLink(options.webOrigin, share.id, options.key)
    });
  }
  return out;
}
async function rotateSharedNoteKey(options) {
  const { api, noteId, owner, index } = options;
  const target2 = { kind: "note", id: noteId };
  const meta = getNoteMetas(index, { includeTrashed: true }).find((m) => m.id === noteId);
  if (!meta) throw new Error(`note ${noteId} is not in this index`);
  const old = noteKeysFromMeta(meta, options.notebookKeys);
  const version = old.version + 1;
  const key = generateKey();
  const ownerWrappedKey = wrapKey(
    options.notebookKeys[0],
    key,
    AD.noteKeyVersion(noteId, version)
  );
  const previousKeyWrap = wrapPreviousKey(key, old.keys[0], "note", noteId, version);
  const members = await activeMembers(api, target2, owner.userId);
  await api.rotateKey(target2, {
    version,
    ownerWrappedKey,
    previousKeyWrap,
    memberKeys: await wrapForMembers(
      api,
      target2,
      key,
      version,
      owner,
      members,
      options.vetting ? { vetting: options.vetting, ...options.confirm ? { confirm: options.confirm } : {} } : void 0
    )
  });
  patchNoteMeta(index, noteId, {
    wrappedKey: ownerWrappedKey,
    keyVersion: version,
    keyChain: [previousKeyWrap, ...meta.keyChain ?? []]
  });
  const keys = { version, keys: [key, ...old.keys] };
  await options.onKeys(keys);
  return keys;
}
async function repairSharedNoteMeta(options) {
  const { noteId, index } = options;
  const meta = getNoteMetas(index, { includeTrashed: true }).find((m) => m.id === noteId);
  if (!meta) return false;
  const res2 = await options.api.getSharedNoteKey(noteId);
  if (res2.version <= (meta.keyVersion ?? 1)) return false;
  unwrapKey(options.notebookKey, res2.ownerWrappedKey, AD.noteKeyVersion(noteId, res2.version));
  patchNoteMeta(index, noteId, {
    wrappedKey: res2.ownerWrappedKey,
    keyVersion: res2.version,
    keyChain: res2.keyChain
  });
  return true;
}

// src/sharing/trust.ts
var norm = (email) => email.trim().toLowerCase();
var res = (t) => `${t.kind}|${t.id}`;
function recordInvite(account, target2, email, at = /* @__PURE__ */ new Date()) {
  accountRoots(account).trust.set(`invite|${res(target2)}|${norm(email)}`, at.toISOString());
}
function recordMemberKey(account, target2, userId, publicKey) {
  const trust = accountRoots(account).trust;
  const key = `member|${res(target2)}|${userId}`;
  if (trust.get(key) !== publicKey) trust.set(key, publicKey);
}
function isTrustMigrated(account) {
  return typeof accountRoots(account).trust.get("migrated") === "string";
}
function markTrustMigrated(account, at = /* @__PURE__ */ new Date()) {
  const trust = accountRoots(account).trust;
  if (!trust.has("migrated")) trust.set("migrated", at.toISOString());
}
function memberTrust(account, target2, member, publicKey) {
  const trust = accountRoots(account).trust;
  const pinned = trust.get(`member|${res(target2)}|${member.userId}`);
  if (typeof pinned === "string") return pinned === publicKey ? "known" : "key_changed";
  if (member.email && trust.has(`invite|${res(target2)}|${norm(member.email)}`)) return "known";
  return isTrustMigrated(account) ? "not_invited" : "known";
}
function recordKeyVersion(account, target2, version) {
  if (version <= 1) return;
  const trust = accountRoots(account).trust;
  const key = `key|${res(target2)}|${version}`;
  if (!trust.has(key)) trust.set(key, true);
}
function knownKeyVersion(account, target2) {
  const prefix = `key|${res(target2)}|`;
  let max = 0;
  for (const k of accountRoots(account).trust.keys()) {
    if (!k.startsWith(prefix)) continue;
    const v = Number(k.slice(prefix.length));
    if (Number.isInteger(v) && v > max) max = v;
  }
  return max;
}
async function migrateMemberTrust(api, account, ownerId) {
  if (isTrustMigrated(account)) return;
  for (const item of await api.sharedByMe()) {
    for (const m of await api.listMembers(item.target)) {
      if (m.userId === ownerId) continue;
      if (m.status === "awaiting_key") {
        if (m.email) recordInvite(account, item.target, m.email);
        continue;
      }
      const pk = await api.getPublicKey(m.userId);
      if (pk) recordMemberKey(account, item.target, m.userId, pk.publicKey);
    }
    for (const inv of await api.listInvitations(item.target)) {
      if (inv.status !== "declined") recordInvite(account, item.target, inv.email);
    }
  }
  markTrustMigrated(account);
}
function accountVetting(account) {
  return {
    check: (target2, member, publicKey) => {
      const t = memberTrust(account(), target2, member, publicKey);
      return t === "known" ? null : t;
    },
    given: (target2, userId, publicKey) => recordMemberKey(account(), target2, userId, publicKey)
  };
}
function isAcceptableSharedSpace(account, item, myUserId) {
  if (item.target.kind !== "space") return false;
  if (item.owner.userId === myUserId) return false;
  return !accountRoots(account).spaces.has(item.target.id);
}

// src/sharing/placement.ts
function reconcileSharedRefs(account, items, spaceId, now = () => /* @__PURE__ */ new Date()) {
  const refs = getSharedRefs(account);
  const known = new Set(refs.map((r) => `${r.kind}:${r.id}`));
  const live = new Set(items.map((i) => `${i.target.kind}:${i.target.id}`));
  const added = [];
  const removed = [];
  let order = refs.filter((r) => r.spaceId === spaceId).reduce((m, r) => Math.max(m, r.order), 0);
  account.transact(() => {
    for (const item of items) {
      if (known.has(`${item.target.kind}:${item.target.id}`)) continue;
      const ref = {
        kind: item.target.kind,
        id: item.target.id,
        spaceId,
        order: ++order,
        addedAt: now().toISOString()
      };
      putSharedRef(account, ref);
      added.push(ref);
    }
    for (const ref of refs) {
      if (live.has(`${ref.kind}:${ref.id}`)) continue;
      deleteSharedRef(account, ref.kind, ref.id);
      removed.push({ kind: ref.kind, id: ref.id });
    }
  });
  return { added, removed };
}
function reconcileSharedSpaces(account, items, myUserId, now = () => /* @__PURE__ */ new Date()) {
  const refs = getSharedSpaceRefs(account);
  const known = new Set(refs.map((r) => r.spaceId));
  const live = new Set(
    items.filter((i) => isAcceptableSharedSpace(account, i, myUserId)).map((i) => i.target.id)
  );
  const added = [];
  const removed = [];
  let order = refs.reduce((m, r) => Math.max(m, r.order), 0);
  account.transact(() => {
    for (const spaceId of live) {
      if (known.has(spaceId)) continue;
      putSharedSpaceRef(account, spaceId, { order: ++order, addedAt: now().toISOString() });
      added.push(spaceId);
    }
    for (const ref of refs) {
      if (live.has(ref.spaceId)) continue;
      deleteSharedSpaceRef(account, ref.spaceId);
      removed.push(ref.spaceId);
    }
  });
  return { added, removed };
}

// src/sharing/spaces.ts
async function rotateSpaceKey(options) {
  const { api, spaceId, owner, masterKey } = options;
  const target2 = { kind: "space", id: spaceId };
  const res2 = await api.getSpaceKey(spaceId);
  let space = ownerSpaceKeys(res2, masterKey, spaceId);
  const entries = await api.listSpaceNotebooks(spaceId);
  let notebooks = new Map(
    entries.map((e) => [e.notebookId, spaceNotebookKeys(e, space.keys, spaceId)])
  );
  if (!res2.keyRotationPending && !options.force) {
    return { rotated: false, newVersion: false, space, notebooks, rekeyedNotes: 0 };
  }
  const since = res2.pendingSinceVersion;
  const resuming = res2.keyRotationPending && since != null && space.version > since;
  if (!resuming) {
    const version = space.version + 1;
    const key = generateKey();
    const members = await activeMembers(api, target2, owner.userId);
    const next = /* @__PURE__ */ new Map();
    const notebookBodies = entries.map((e) => {
      const old = notebooks.get(e.notebookId);
      const v = old.version + 1;
      const nbKey = generateKey();
      next.set(e.notebookId, { version: v, keys: [nbKey, ...old.keys] });
      return {
        notebookId: e.notebookId,
        version: v,
        spaceWrappedKey: wrapNotebookKeyForSpace(key, nbKey, spaceId, e.notebookId, v),
        previousKeyWrap: wrapPreviousKey(nbKey, old.keys[0], "notebook", e.notebookId, v),
        ...e.mode === "agent" ? { notebookKey: toBase64Url(nbKey) } : {}
      };
    });
    await api.rotateSpace(spaceId, {
      version,
      ownerWrappedKey: wrapSpaceKey(masterKey, key, spaceId, version),
      previousKeyWrap: wrapPreviousKey(key, space.keys[0], "space", spaceId, version),
      memberKeys: await wrapForMembers(
        api,
        target2,
        key,
        version,
        owner,
        members,
        options.vetting ? { vetting: options.vetting, ...options.confirm ? { confirm: options.confirm } : {} } : void 0
      ),
      notebooks: notebookBodies
    });
    space = { version, keys: [key, ...space.keys] };
    notebooks = next;
    await options.onKeys({ space, notebooks });
  }
  let rekeyed = 0;
  for (const [notebookId, keys] of notebooks) {
    const notes = rekeyNotes(await options.notebookIndex(notebookId), keys);
    await options.whenIndexSent(notebookId);
    if (notes.size) await options.onKeys({ notes });
    rekeyed += notes.size;
  }
  await api.rotateSpaceDone(spaceId);
  return { rotated: true, newVersion: !resuming, space, notebooks, rekeyedNotes: rekeyed };
}
async function moveNotebookIntoSpace(options) {
  const { api, spaceId, notebookId, masterKey } = options;
  const notebook = ownerNotebookKeys(await api.getNotebookKey(notebookId), masterKey, notebookId);
  const space = ownerSpaceKeys(await api.getSpaceKey(spaceId), masterKey, spaceId);
  await api.putSpaceNotebook(spaceId, notebookId, {
    version: notebook.version,
    spaceWrappedKey: wrapNotebookKeyForSpace(
      space.keys[0],
      notebook.keys[0],
      spaceId,
      notebookId,
      notebook.version
    )
  });
  const ref = getNotebookRef(options.account, notebookId);
  if (ref) {
    const { spaceId: _from, ...rest } = ref;
    putSpaceNotebookRef(options.spaceDoc, rest);
    deleteNotebookRef(options.account, notebookId);
  }
  return notebook;
}
async function moveNotebookOutOfSpace(options) {
  const { api, spaceId, notebookId, masterKey } = options;
  const space = ownerSpaceKeys(await api.getSpaceKey(spaceId), masterKey, spaceId);
  const entry = (await api.listSpaceNotebooks(spaceId)).find((e) => e.notebookId === notebookId);
  if (!entry) throw new Error(`notebook ${notebookId} is not in space ${spaceId}`);
  const old = spaceNotebookKeys(entry, space.keys, spaceId);
  const version = old.version + 1;
  const key = generateKey();
  await api.moveOutSpaceNotebook(spaceId, notebookId, {
    version,
    ownerWrappedKey: wrapKey(masterKey, key, AD.notebookKeyVersion(notebookId, version)),
    previousKeyWrap: wrapPreviousKey(key, old.keys[0], "notebook", notebookId, version),
    ...entry.mode === "agent" ? { notebookKey: toBase64Url(key) } : {}
  });
  const keys = { version, keys: [key, ...old.keys] };
  await options.onKeys(keys);
  const ref = getSpaceNotebookRef(options.spaceDoc, notebookId);
  if (ref) {
    const moved = { ...ref, spaceId: options.toSpaceId };
    putNotebookRef(options.account, moved);
    deleteSpaceNotebookRef(options.spaceDoc, notebookId);
  }
  return keys;
}

// src/sharing/owner.ts
async function maintainOwnedShares(options) {
  const out = [];
  for (const item of await options.api.sharedByMe()) {
    try {
      out.push(
        item.target.kind === "notebook" ? await maintainNotebook(options, item) : await maintainNote(options, item)
      );
    } catch (error) {
      out.push({ target: item.target, error });
    }
  }
  return out;
}
async function maintainNotebook(o, item) {
  const { target: target2 } = item;
  const result = { target: target2, rotated: false, repaired: false, delivered: [] };
  let keys = ownerNotebookKeys(await o.api.getNotebookKey(target2.id), o.masterKey, target2.id);
  if (item.keyRotationPending) {
    const r = await rotateNotebookKey({
      api: o.api,
      notebookId: target2.id,
      owner: o.owner,
      masterKey: o.masterKey,
      index: await o.notebookIndex(target2.id),
      agentMode: o.isAgentMode(target2.id),
      whenIndexSent: () => o.whenIndexSent(target2.id),
      onKeys: async (k) => {
        if (k.notebook) await o.onKeys(target2, k.notebook);
        for (const [noteId, nk] of k.notes ?? []) await o.onKeys({ kind: "note", id: noteId }, nk);
      },
      ...vettingOf(o, target2)
    });
    keys = r.notebook;
    result.rotated = r.rotated;
    if (r.rotated) await relink(o, target2, keys);
  }
  if (item.awaitingKey > 0) result.delivered = await deliver(o, target2, keys);
  return result;
}
async function maintainNote(o, item) {
  const { target: target2 } = item;
  const result = { target: target2, rotated: false, repaired: false, delivered: [] };
  const notebookId = await o.notebookOfNote(target2.id);
  if (!notebookId) return { ...result, skipped: "notebook of the note is not available" };
  const index = await o.notebookIndex(notebookId);
  const notebookKeys = ownerNotebookKeys(
    await o.api.getNotebookKey(notebookId),
    o.masterKey,
    notebookId
  ).keys;
  const meta = getNoteMeta(index, target2.id);
  if (!meta) return { ...result, skipped: "note is not in its notebook index" };
  if (item.keyVersion > (meta.keyVersion ?? 1)) {
    result.repaired = await repairSharedNoteMeta({
      api: o.api,
      noteId: target2.id,
      index,
      notebookKey: notebookKeys[0]
    });
  }
  let keys = noteKeysFromMeta(getNoteMeta(index, target2.id), notebookKeys);
  if (item.keyRotationPending) {
    keys = await rotateSharedNoteKey({
      api: o.api,
      noteId: target2.id,
      owner: o.owner,
      index,
      notebookKeys,
      onKeys: (k) => o.onKeys(target2, k),
      ...vettingOf(o, target2)
    });
    result.rotated = true;
    await relink(o, target2, keys);
  }
  if (result.repaired && !result.rotated) await o.onKeys(target2, keys);
  if (item.awaitingKey > 0) result.delivered = await deliver(o, target2, keys);
  return result;
}
async function relink(o, target2, keys) {
  if (!o.links) return;
  const links = await recreateShareLinks({
    api: o.api,
    target: target2,
    key: keys.keys[0],
    webOrigin: o.links.webOrigin
  });
  if (links.length)
    o.links.onRecreated(
      target2,
      links.map((l) => l.url)
    );
}
function vettingOf(o, target2) {
  return {
    ...o.vetting ? { vetting: o.vetting } : {},
    ...o.confirm ? { confirm: (m, reason) => o.confirm(target2, m, reason) } : {}
  };
}
async function deliver(o, target2, keys) {
  const members = await deliverAwaitingKeys({
    api: o.api,
    target: target2,
    keys,
    owner: o.owner,
    ...o.confirm ? { confirm: (m, reason) => o.confirm(target2, m, reason) } : {},
    ...o.vetting ? { vetting: o.vetting } : {}
  });
  return members.map((m) => m.userId);
}
async function maintainOwnedSpaces(options) {
  const o = options;
  const out = [];
  for (const item of await o.api.sharedByMeAll()) {
    const { target: target2 } = item;
    if (target2.kind !== "space") continue;
    try {
      let rotated = false;
      let keys;
      if (item.keyRotationPending) {
        if (!o.rotation) {
          out.push({ target: target2, delivered: [], skipped: "space key rotation pending" });
          continue;
        }
        keys = (await rotateSpaceKey({
          api: o.api,
          spaceId: target2.id,
          owner: o.owner,
          masterKey: o.masterKey,
          ...o.rotation,
          ...o.vetting ? { vetting: o.vetting } : {},
          ...o.confirm ? { confirm: (m, r) => o.confirm(target2, m, r) } : {}
        })).space;
        rotated = true;
      } else {
        keys = ownerSpaceKeys(await o.api.getSpaceKey(target2.id), o.masterKey, target2.id);
      }
      const delivered = item.awaitingKey > 0 ? await deliverAwaitingKeys({
        api: o.api,
        target: target2,
        keys,
        owner: o.owner,
        ...o.vetting ? { vetting: o.vetting } : {},
        ...o.confirm ? { confirm: (m, r) => o.confirm(target2, m, r) } : {}
      }) : [];
      out.push({
        target: target2,
        delivered: delivered.map((m) => m.userId),
        ...rotated ? { rotated } : {}
      });
    } catch (error) {
      out.push({ target: target2, error });
    }
  }
  return out;
}

export { ApiRequestError, MemberNotConfirmedError, accountVetting, activeMembers, createHttpClient, createSharingApi, deliverAwaitingKeys, ensureAccountKeyPair, isAcceptableSharedSpace, isTrustMigrated, knownKeyVersion, maintainOwnedShares, maintainOwnedSpaces, markTrustMigrated, memberTrust, migrateMemberTrust, moveNotebookIntoSpace, moveNotebookOutOfSpace, noteKeysFromMeta, ownerNotebookKeys, ownerSpaceKeys, reconcileSharedRefs, reconcileSharedSpaces, recordInvite, recordKeyVersion, recordMemberKey, recreateShareLinks, rekeyNotes, repairSharedNoteMeta, rotateNotebookKey, rotateSharedNoteKey, rotateSpaceKey, sharedItemKeys, spaceNotebookKeys, wrapForMembers };
//# sourceMappingURL=chunk-67DJBPGR.js.map
//# sourceMappingURL=chunk-67DJBPGR.js.map